import { defineComponent } from 'vue';
import { QBtn, QTabs, QTab, QTabPanels, QTabPanel, QBanner, QIcon } from 'quasar';
import MethodSignaturePanel from './MethodSignaturePanel.jsx';
import MethodTouchpointsPanel from './MethodTouchpointsPanel.jsx';
import ProjectSettingsPanel from './ProjectSettingsPanel.jsx';
import ProjectSandboxPanel from './ProjectSandboxPanel.jsx';
import GlobalVarsPanel from './GlobalVarsPanel.jsx';
import { SENTENCE_CASE_BUTTON_CLASS } from '../../../../../Shared/buttonAppearance.js';
import '../../../css/BasicButton.css';
import '../styles/flowSurfaceSwitch.css';

/**
 * Two-level config surface: Method (Signature | Touchpoints) | Project (Settings | Shared variables | Release management).
 */
export default defineComponent({
    name: 'flow-v2-config-panel',
    components: {
        QBtn, QTabs, QTab, QTabPanels, QTabPanel, QBanner, QIcon,
        MethodSignaturePanel,
        MethodTouchpointsPanel,
        ProjectSettingsPanel,
        ProjectSandboxPanel,
        GlobalVarsPanel,
    },
    props: {
        configScope: { type: String, default: 'method' },
        configTab: { type: String, default: 'signature' },
        flowId: { type: String, default: null },
        flowType: { type: String, default: null },
        flowName: { type: String, default: '' },
        projectId: { type: String, default: null },
        projectName: { type: String, default: '' },
        channelType: { type: String, default: 'Call' },
        channelConfig: { type: Object, default: null },
        root: { type: Object, default: null },
        scopeitems: { type: Object, default: null },
        controlscope: { type: Object, default: null },
        readOnly: { type: Boolean, default: false },
    },
    emits: [
        'update:configScope',
        'update:configTab',
        'close',
        'method-saved',
        'project-saved',
        'open-project-releases',
    ],
    methods: {
        normalizeProjectTab(tab) {
            // Accept legacy "sandbox" tab id from older sessions
            if (tab === 'releases' || tab === 'sandbox')
                return 'releases';
            if (tab === 'sharedvars')
                return 'sharedvars';
            return 'settings';
        },
        setScope(scope) {
            this.$emit('update:configScope', scope);
            if (scope === 'method') {
                const tab = this.configTab === 'settings' || this.configTab === 'sandbox'
                    || this.configTab === 'releases' || this.configTab === 'sharedvars'
                    ? 'signature'
                    : this.configTab;
                this.$emit('update:configTab', tab === 'touchpoints' ? 'touchpoints' : 'signature');
            }
            else {
                const tab = this.configTab === 'signature' || this.configTab === 'touchpoints'
                    ? 'settings'
                    : this.configTab;
                this.$emit('update:configTab', this.normalizeProjectTab(tab));
            }
        },
        setTab(tab) {
            if (tab === 'sandbox')
                tab = 'releases';
            this.$emit('update:configTab', tab);
        },
        breadcrumbProjectClick() {
            this.setScope('project');
            this.$emit('update:configTab', 'releases');
        },
    },
    render() {
        const hasMethod = !!(this.flowId && (this.flowType === 'FlowHeader' || this.flowType === 'APIHeader'));
        const hasProject = !!this.projectId;
        const scope = this.configScope === 'project' ? 'project' : 'method';
        const methodTab = this.configTab === 'touchpoints' ? 'touchpoints' : 'signature';
        const projectTab = this.normalizeProjectTab(this.configTab);

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, background: '#fafafa', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderBottom: '1px solid #e0e0e0' }}>
                    <div style={{ flex: 1, minWidth: 0 }} class="text-body2 text-grey-8">
                        {hasProject ? (
                            <span>
                                <a href="#" style={{ color: '#3949ab', textDecoration: 'none' }}
                                    onClick={(e) => { e.preventDefault(); this.breadcrumbProjectClick(); }}>
                                    {this.projectName || this.projectId}
                                </a>
                                {hasMethod ? <span> / {this.flowName || this.flowId}</span> : null}
                            </span>
                        ) : (
                            <span>{this.flowName || 'Configuration'}</span>
                        )}
                    </div>
                    <div class="flow-v2-surface-switch">
                        <QBtn
                            label="Method"
                            unelevated
                            no-caps
                            disable={!hasMethod}
                            class={`${SENTENCE_CASE_BUTTON_CLASS} q-my-xs ThemeRightConnector`}
                            color={scope === 'method' ? 'primary' : 'grey-3'}
                            text-color={scope === 'method' ? 'white' : 'grey-8'}
                            onClick={() => this.setScope('method')}
                        />
                        <QBtn
                            label="Project"
                            unelevated
                            no-caps
                            disable={!hasProject}
                            class={`${SENTENCE_CASE_BUTTON_CLASS} q-my-xs ThemeLeftConnector`}
                            color={scope === 'project' ? 'primary' : 'grey-3'}
                            text-color={scope === 'project' ? 'white' : 'grey-8'}
                            onClick={() => this.setScope('project')}
                        />
                    </div>
                </div>

                {scope === 'method' && !hasMethod ? (
                    <QBanner class="bg-grey-2 q-ma-md" rounded>
                        {{
                            avatar: () => <QIcon name="mdi-information-outline" color="primary" />,
                            default: () => 'Select a flow or API in the folder tree to edit method settings.',
                        }}
                    </QBanner>
                ) : null}

                {scope === 'method' && hasMethod ? (
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                        <div class="c-BasicTab bg-white">
                            <QTabs
                                dense
                                align="left"
                                breakpoint={0}
                                indicatorColor="transparent"
                                style={{ color: 'var(--v-secondaryText-base)' }}
                                modelValue={methodTab}
                                onUpdate:modelValue={(t) => this.setTab(t)}
                            >
                                <QTab name="signature" label="Signature" class="text-none" />
                                <QTab name="touchpoints" label="Touchpoints" class="text-none" />
                            </QTabs>
                        </div>
                        <div style={{
                            flex: 1,
                            minHeight: 0,
                            overflow: methodTab === 'signature' ? 'hidden' : 'auto',
                            padding: '12px',
                            display: methodTab === 'signature' ? 'flex' : undefined,
                            flexDirection: methodTab === 'signature' ? 'column' : undefined,
                            pointerEvents: this.readOnly ? 'none' : undefined,
                        }}>
                            {methodTab === 'signature' ? (
                                <MethodSignaturePanel
                                    flowId={this.flowId}
                                    flowType={this.flowType}
                                    root={this.root}
                                    scopeitems={this.scopeitems}
                                    controlscope={this.controlscope}
                                    channelConfig={this.channelConfig}
                                    onSaved={(data) => this.$emit('method-saved', data)}
                                />
                            ) : (
                                <MethodTouchpointsPanel
                                    flowId={this.flowId}
                                    flowType={this.flowType}
                                    projectId={this.projectId}
                                    channelType={this.channelType}
                                    channelConfig={this.channelConfig}
                                    onOpenProjectReleases={() => {
                                        this.setScope('project');
                                        this.$emit('update:configTab', 'releases');
                                        this.$emit('open-project-releases');
                                    }}
                                />
                            )}
                        </div>
                    </div>
                ) : null}

                {scope === 'project' && hasProject ? (
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0 }}>
                        <div class="c-BasicTab bg-white">
                            <QTabs
                                dense
                                align="left"
                                breakpoint={0}
                                indicatorColor="transparent"
                                style={{ color: 'var(--v-secondaryText-base)' }}
                                modelValue={projectTab}
                                onUpdate:modelValue={(t) => this.setTab(t)}
                            >
                                <QTab name="settings" label="Settings" class="text-none" />
                                <QTab name="sharedvars" label="Shared variables" class="text-none" />
                                <QTab name="releases" label="Release management" class="text-none" />
                            </QTabs>
                        </div>
                        <div style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: projectTab === 'sharedvars' || projectTab === 'settings' ? 'hidden' : 'auto',
                            padding: '12px',
                            display: projectTab === 'sharedvars' || projectTab === 'settings' ? 'flex' : undefined,
                            flexDirection: projectTab === 'sharedvars' || projectTab === 'settings' ? 'column' : undefined,
                            minHeight: 0,
                            pointerEvents: this.readOnly ? 'none' : undefined,
                        }}>
                            {projectTab === 'settings' ? (
                                <ProjectSettingsPanel
                                    projectId={this.projectId}
                                    channel={this.channelConfig}
                                    root={this.root}
                                    scopeitems={this.scopeitems}
                                    controlscope={this.controlscope}
                                    onSaved={(data) => this.$emit('project-saved', data)}
                                />
                            ) : projectTab === 'sharedvars' ? (
                                <GlobalVarsPanel projectId={this.projectId} />
                            ) : (
                                <ProjectSandboxPanel
                                    projectId={this.projectId}
                                    projectName={this.projectName}
                                />
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
        );
    },
});
